import { Component, EventEmitter, Input, Output, signal, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';

import { HttpClientModule } from '@angular/common/http';
import { ChatService, TranscriptionRequest } from 'src/app/chat-bot/chat.service';


export interface AudioRecording {
  audioBlob: Blob;
  duration: number;
}

@Component({
  selector: 'app-audio-recorder',
  standalone: true,
  imports: [MatIcon, HttpClientModule],
  templateUrl: './audio-recorder.component.html',
  styleUrls: ['./audio-recorder.component.scss']
})
export class AudioRecorderComponent {
  // Services
  private readonly chatService = inject(ChatService);

  // Input properties
  @Input() disabled: boolean = false;
  @Input() buttonSize: 'small' | 'medium' | 'large' = 'medium';
  @Input() buttonColor: string = 'branding-color-12';
  @Input() buttonBackground: string = 'branding-background-11';
  @Input() sessionId: string = '';

  // Output events
  @Output() recordingStarted = new EventEmitter<void>();
  @Output() recordingStopped = new EventEmitter<AudioRecording>();
  @Output() recordingCancelled = new EventEmitter<void>();
  @Output() recordingError = new EventEmitter<string>();
  @Output() transcriptionComplete = new EventEmitter<string>();
  @Output() transcriptionError = new EventEmitter<string>();

  // Signals for reactive state
  isRecording = signal(false);
  recordingTime = signal(0);
  canRecord = signal(false);
  isTranscribing = signal(false);

  // Private state variables
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private recordingInterval: any;
  private stream: MediaStream | null = null;

  ngOnInit() {
    this.checkRecordingCapability();
  }

  ngOnDestroy() {
    this.stopRecording();
  }

  private checkRecordingCapability(): void {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      this.canRecord.set(true);
    } else {
      console.warn('Voice recording not supported in this browser');
      this.canRecord.set(false);
    }
  }

  startRecording(): void {
    if (!this.canRecord() || this.disabled) {
      console.warn('Voice recording not supported');
      return;
    }

    this.audioChunks = [];

    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        this.stream = stream;
        this.mediaRecorder = new MediaRecorder(stream);

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          this.handleRecordingComplete();
        };

        this.mediaRecorder.start();
        this.isRecording.set(true);
        this.recordingTime.set(0);
        this.startRecordingTimer();
        this.recordingStarted.emit();

        console.log('Voice recording started');
      })
      .catch((error) => {
        console.error('Error starting voice recording:', error);
        this.recordingError.emit('Failed to start recording');
      });
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.isRecording()) {
      this.mediaRecorder.stop();
      this.stopRecordingTimer();
      this.isRecording.set(false);

      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }

      console.log('Voice recording stopped');
    }
  }

  private handleRecordingComplete(): void {
    if (this.audioChunks.length === 0) {
      this.recordingError.emit('No audio data recorded');
      return;
    }

    const audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
    const recordingDuration = this.recordingTime();

    // Emit the recording data
    this.recordingStopped.emit({
      audioBlob,
      duration: recordingDuration
    });

    // Automatically transcribe the audio
    this.transcribeAudio(audioBlob, recordingDuration);
  }

  /**
   * Transcribes the recorded audio using the speech-to-text service
   */
  private transcribeAudio(audioBlob: Blob, duration: number): void {
    if (!this.sessionId) {
      this.transcriptionError.emit('Session ID is required for transcription');
      return;
    }

    this.isTranscribing.set(true);

    const request: TranscriptionRequest = {
      audio: audioBlob,
      duration: duration,
      sessionId: this.sessionId,
      timestamp: new Date().toISOString()
    };

    this.chatService.transcribeAudio(request).subscribe({
      next: (response) => {
        this.isTranscribing.set(false);
        if (response.text) {
          this.transcriptionComplete.emit(response.text);
        } else {
          this.transcriptionError.emit(response.error || 'Transcription failed');
        }
      },
      error: (error) => {
        this.isTranscribing.set(false);
        console.error('Transcription error:', error);
        this.transcriptionError.emit('Transcription service error');
      }
    });
  }

  private startRecordingTimer(): void {
    this.recordingInterval = setInterval(() => {
      this.recordingTime.update(time => time + 1);
    }, 1000);
  }

  private stopRecordingTimer(): void {
    if (this.recordingInterval) {
      clearInterval(this.recordingInterval);
      this.recordingInterval = null;
    }
  }

  toggleVoiceRecording(): void {
    if (this.isRecording()) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

}
